/**
 * "Download CSV" button shown on the Asset Positions tab (Fulcrum-internal +
 * superusers).
 *
 * Fetches the timeseries JSON, serialises a one-row-per-date CSV and triggers
 * a browser download.
 */
import { useState } from 'react';

import { endpoints } from '@/lib/api/endpoints';
import { downloadCsv } from '@/lib/csv';
import type { TimeseriesPayload } from '../types';

interface ExposuresDownloadButtonProps {
  fundId: string;
}

/** Build a `date,Equities,Bonds,…` CSV from the wide timeseries payload. */
function serialiseTimeseriesCsv(data: TimeseriesPayload): string {
  const exposures = data.exposures;
  if (!exposures?.dates) return '';
  const series = exposures.series ?? {};
  const keys = Object.keys(series);
  const header = ['date', ...keys].join(',');
  const rows = exposures.dates.map((date, i) => {
    const cells = keys.map((k) => {
      const v = series[k]?.[i];
      if (v === null || v === undefined || Number.isNaN(v)) return '';
      return String(v);
    });
    return [date, ...cells].join(',');
  });
  return [header, ...rows].join('\n');
}

export default function ExposuresDownloadButton({
  fundId,
}: ExposuresDownloadButtonProps): JSX.Element {
  const [error, setError] = useState<string | null>(null);

  const handleClick = async (): Promise<void> => {
    setError(null);
    try {
      const url = endpoints.mars.portfolioTimeseries(fundId);
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = (await resp.json()) as { success: boolean; data: TimeseriesPayload };
      if (!json.success) throw new Error('Download failed');

      const today = new Date().toISOString().slice(0, 10);
      downloadCsv(serialiseTimeseriesCsv(json.data), `mars_portfolio_${fundId}_${today}.csv`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  return (
    <div className="inline-flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={handleClick}
        title="Download portfolio data as CSV"
        className="inline-flex items-center gap-2 bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] text-white px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          viewBox="0 0 16 16"
          aria-hidden
        >
          <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
          <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
        </svg>
        Download CSV
      </button>
      {error && (
        <span role="alert" className="text-xs text-red-600">
          Error downloading data: {error}
        </span>
      )}
    </div>
  );
}
